// Per-order production sheet ("fiche de production") — Phase 5, 2026-08-01.
//
// WHY THIS EXISTS
// The printer's ZIP contained PDFs only: `_couverture.pdf` and `_interieur.pdf`.
// A paid add-on appeared as an amber badge on the web page and NOWHERE in the
// archive, so the instruction was lost the second the printer downloaded the
// files and worked offline from a folder. An order could be paid for and printed
// flat. This sheet travels INSIDE the ZIP, so the instruction cannot be
// separated from the job.
//
// Not to be confused with the universal "fiche de montage"
// (assemblyTemplate.ts): that is ONE die-line sheet printed once and reused for
// every hardcover — board sizes, fold marks, corner snips, no order number. This
// is per-order and carries the paperwork.
//
// A4 portrait, one page, pdf-lib with a built-in font so nothing has to be
// fetched. Deliberately plain: it is printed on office paper and read at arm's
// length in a workshop.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export interface ProductionSheetAlbum {
  album_index: number;
  destination: string | null;
}

export interface ProductionSheetUpsell {
  /** The printer's own wording (products.printer_label), not marketing's. */
  label: string;
  quantity: number;
}

export interface ProductionSheetOrder {
  order_name: string;
  bundle: string | null;
  albums: ProductionSheetAlbum[];
  upsells?: ProductionSheetUpsell[];
}

/**
 * pdf-lib's standard fonts use WinAnsi, which THROWS on any character outside
 * it. Customer destinations are free text and routinely contain emoji, curly
 * quotes or Arabic — none of which a production sheet needs. Strip to a safe set
 * rather than let one exotic character fail the whole archive.
 */
function safe(text: string): string {
  return String(text ?? '')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, '')
    .trim();
}

const A4 = { w: 595.28, h: 841.89 };
const MARGIN = 48;
const INK = rgb(0.1, 0.1, 0.1);
const MUTED = rgb(0.42, 0.42, 0.42);
const RULE = rgb(0.85, 0.85, 0.85);
const ALERT = rgb(0.62, 0.36, 0.02);
const ALERT_BG = rgb(0.99, 0.95, 0.85);

export async function generateProductionSheetPdf(
  order: ProductionSheetOrder,
  printedOn: Date,
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([A4.w, A4.h]);
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  let y = A4.h - MARGIN;

  const line = (text: string, size: number, f = font, color = INK, gap = 6) => {
    page.drawText(safe(text), { x: MARGIN, y, size, font: f, color });
    y -= size + gap;
  };

  line('FICHE DE PRODUCTION', 20, bold);
  y -= 2;
  page.drawLine({
    start: { x: MARGIN, y }, end: { x: A4.w - MARGIN, y },
    thickness: 1, color: RULE,
  });
  y -= 22;

  line(`Commande ${order.order_name}`, 26, bold);
  y -= 4;

  const albumCount = order.albums.length || 1;
  line(`Formule : ${order.bundle ?? '-'}   |   ${albumCount} album(s)`, 12, font, MUTED);
  line(`Edite le ${printedOn.toLocaleDateString('fr-FR')}`, 10, font, MUTED, 18);

  // ── Paid options — the reason this sheet exists. Boxed and first, above the
  //    album list, so it cannot be skimmed past.
  const upsells = order.upsells ?? [];
  if (upsells.length > 0) {
    const boxH = 34 + upsells.length * 20;
    page.drawRectangle({
      x: MARGIN, y: y - boxH + 14, width: A4.w - MARGIN * 2, height: boxH,
      color: ALERT_BG, borderColor: ALERT, borderWidth: 1.5,
    });
    y -= 6;
    page.drawText(safe('TRAVAIL SUPPLEMENTAIRE - A NE PAS OUBLIER'), {
      x: MARGIN + 14, y, size: 12, font: bold, color: ALERT,
    });
    y -= 22;
    for (const u of upsells) {
      page.drawText(safe(`- ${u.label}   (x${u.quantity})`), {
        x: MARGIN + 14, y, size: 12, font: bold, color: INK,
      });
      y -= 20;
    }
    y -= 20;
  } else {
    line('Aucune option payante sur cette commande.', 11, font, MUTED, 18);
  }

  // ── Albums in the order, matching the PDF filenames in this same ZIP so the
  //    printer can tick them off one by one.
  line('ALBUMS', 12, bold, MUTED, 10);
  page.drawLine({
    start: { x: MARGIN, y: y + 6 }, end: { x: A4.w - MARGIN, y: y + 6 },
    thickness: 0.5, color: RULE,
  });
  y -= 6;

  for (const a of order.albums) {
    const dest = a.destination ? ` - ${a.destination}` : '';
    line(`[  ]  Album ${a.album_index} sur ${albumCount}${dest}`, 12, font, INK, 8);
  }

  y -= 16;
  page.drawLine({
    start: { x: MARGIN, y }, end: { x: A4.w - MARGIN, y },
    thickness: 0.5, color: RULE,
  });
  y -= 16;
  line('Les fichiers PDF de cette commande se trouvent dans le meme dossier.', 10, font, MUTED);

  return doc.save();
}
